import { TextButton } from "@clipboard-health/ui-components";
// eslint-disable-next-line no-restricted-imports
import { Box, Link, Stack, Tab, Tabs } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useScrollRestoration } from "@src/appV2/lib/utils/useScrollRestoration";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type Worker } from "@src/appV2/Worker/api/types";
import { isEqual } from "lodash";

import { OnboardingRequirementsBanner } from "../../PlacementCandidate/components/OnboardingRequirementsBanner";
import { type GetPlacementsForWorkplaceQuery } from "../types/fetchPlacements.schema";
import { AppliedPlacementListView } from "./AppliedPlacementListView";
import { InterviewsCarousel } from "./InterviewsCarousel";
import { JobsTopButtons } from "./JobsTopButtons";
import { OpenPlacementListView } from "./OpenPlacementListView";

interface PlacementsListContainerProps {
  placementCandidate: PlacementCandidate;
  worker: Worker;
  filter: GetPlacementsForWorkplaceQuery["filter"];
  setFilter: (filter: GetPlacementsForWorkplaceQuery["filter"]) => void;

  showApplications: boolean;
  setShowApplications: (value: boolean) => void;
}

export function PlacementsListContainer(props: PlacementsListContainerProps) {
  const { placementCandidate, worker, filter, setFilter, showApplications, setShowApplications } =
    props;

  useScrollRestoration({ key: "jobs-tab", containerId: "jobs-list-container" });

  const nonEmptyFilter = Object.fromEntries(
    Object.entries(filter ?? {}).filter(([_, value]) => value)
  );

  return (
    <Stack
      sx={{
        overflowY: "auto",
        paddingY: 5,
        backgroundColor: (theme) => theme.palette.background.primary,
        flex: 1,
      }}
      spacing={5}
      id="jobs-list-container"
    >
      <JobsTopButtons placementCandidate={placementCandidate} worker={worker} />

      <Stack spacing={6}>
        <InterviewsCarousel worker={worker} />
        <Stack spacing={6} sx={{ paddingX: 5 }}>
          <Tabs
            value={showApplications ? "applications" : "jobs"}
            variant="fullWidth"
            sx={{
              width: "100%",
            }}
            onChange={(_, newValue) => {
              setShowApplications(newValue === "applications");
            }}
          >
            <Tab value="jobs" label="Jobs" />
            <Tab value="applications" label="Applications" />
          </Tabs>
          <Box>
            {!isEqual(nonEmptyFilter, {}) && (
              <Stack direction="row" justifyContent="flex-start">
                <TextButton
                  LinkComponent={Link}
                  size="medium"
                  onClick={() => {
                    setFilter({});
                    logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
                      placementCandidateId: placementCandidate.id,
                      action: "filters_cleared",
                      filters: { ...filter, distanceInMiles: filter?.distanceInMiles?.$lt },
                    });
                  }}
                >
                  Clear filters to view all {showApplications ? "applied jobs" : "jobs"}
                </TextButton>
              </Stack>
            )}
          </Box>

          <OnboardingRequirementsBanner workerId={worker.userId} />

          {!showApplications && (
            <OpenPlacementListView
              placementCandidate={placementCandidate}
              worker={worker}
              filter={filter}
              showApplications={showApplications}
            />
          )}

          {showApplications && (
            <AppliedPlacementListView
              placementCandidate={placementCandidate}
              worker={worker}
              filter={filter}
              showApplications={showApplications}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
